'use client';

// @boty/ui - ChatBubble component
// RTL-aware chat message bubbles for user and bot messages
// Story 4.5: Mobile-Optimized Bot Interface - responsive sizing

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import { MarkdownContent } from './MarkdownContent';

/**
 * Shared styles for the streaming cursor animation
 * Extracted to avoid duplication between markdown and plain text branches
 */
const streamingCursorSx = {
  display: 'inline-block',
  width: 8,
  height: 16,
  backgroundColor: 'grey.500',
  ml: 0.5,
  animation: 'chatBubbleBlink 1s infinite',
  verticalAlign: 'text-bottom',
  '@keyframes chatBubbleBlink': {
    '0%, 50%': { opacity: 1 },
    '51%, 100%': { opacity: 0 },
  },
} as const;

/**
 * Chat bubble sender type
 */
export type ChatBubbleSender = 'user' | 'bot';

export interface ChatBubbleProps {
  /**
   * The message content to display
   */
  children: ReactNode;
  /**
   * Who sent the message - determines alignment and styling
   */
  sender: ChatBubbleSender;
  /**
   * Optional avatar element or URL
   */
  avatar?: ReactNode | string;
  /**
   * Optional timestamp to display
   */
  timestamp?: string;
  /**
   * Whether the message is currently streaming (shows blinking cursor)
   */
  isStreaming?: boolean;
  /**
   * Whether to render children as markdown (for bot responses)
   */
  markdown?: boolean;
  /**
   * Callback when a link in markdown content is clicked.
   * If provided, prevents default navigation.
   */
  onLinkClick?: (url: string) => void;
  /**
   * Additional styles for the outer container
   */
  sx?: SxProps<Theme>;
}

/**
 * ChatBubble - RTL-aware chat message component
 *
 * Automatically handles message alignment based on text direction:
 * - LTR: User messages align right, bot messages align left
 * - RTL: User messages align left, bot messages align right
 *
 * @param children - The message content
 * @param sender - 'user' or 'bot' to determine styling and alignment
 * @param avatar - Optional avatar (ReactNode or image URL)
 * @param timestamp - Optional timestamp string
 * @param sx - Additional styles for the container
 *
 * @example
 * ```tsx
 * // User message
 * <ChatBubble sender="user">
 *   Hello, how can I help?
 * </ChatBubble>
 *
 * // Bot message with avatar
 * <ChatBubble sender="bot" avatar={<BotAvatar />}>
 *   I'm here to assist you with any questions!
 * </ChatBubble>
 *
 * // Message with timestamp
 * <ChatBubble sender="user" timestamp="10:30 AM">
 *   Thanks for your help!
 * </ChatBubble>
 * ```
 */
export function ChatBubble({
  children,
  sender,
  avatar,
  timestamp,
  isStreaming = false,
  markdown = false,
  onLinkClick,
  sx,
}: ChatBubbleProps) {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const isUser = sender === 'user';

  // Determine alignment based on sender and direction
  // User: flex-end in LTR (right), flex-start in RTL (left)
  // Bot: ALWAYS flex-start (left side) like WhatsApp - bot messages don't swap in RTL
  const justifyContent = isUser
    ? isRtl
      ? 'flex-start'
      : 'flex-end'
    : 'flex-start'; // Bot always left

  // Avatar position: opposite side of bubble
  // User: avatar on right in LTR, left in RTL
  // Bot: avatar ALWAYS on left (consistent with justifyContent)
  const flexDirection = isUser
    ? isRtl
      ? 'row'
      : 'row-reverse'
    : 'row'; // Bot avatar always on left

  // Render avatar element
  const renderAvatar = () => {
    if (!avatar) return null;

    if (typeof avatar === 'string') {
      return (
        <Avatar
          src={avatar}
          alt={isUser ? 'User' : 'Bot'}
          sx={{
            width: 32,
            height: 32,
            bgcolor: isUser ? 'primary.main' : 'secondary.main',
          }}
        />
      );
    }

    return avatar;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent,
        width: '100%',
        marginBlockEnd: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection,
          alignItems: 'flex-end',
          gap: 1,
          // Responsive maxWidth: wider on mobile for better readability
          maxWidth: { xs: '88%', sm: '80%', md: '70%' },
        }}
      >
        {renderAvatar()}

        <Box>
          <Box
            sx={{
              // Responsive padding: more compact on mobile
              paddingInline: { xs: 1.5, sm: 2 },
              paddingBlock: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              backgroundColor: isUser
                ? 'primary.main'
                : theme.palette.mode === 'dark'
                  ? 'grey.800'
                  : 'grey.100',
              color: isUser
                ? 'primary.contrastText'
                : theme.palette.mode === 'dark'
                  ? 'grey.100'
                  : 'text.primary',
              // RTL-aware border radius for bubble tail effect
              ...(isUser
                ? {
                    borderEndEndRadius: 0.5,
                  }
                : {
                    borderEndStartRadius: 0.5,
                  }),
            }}
          >
            {markdown && typeof children === 'string' ? (
              <Box dir="auto" sx={{ textAlign: 'start' }}>
                <MarkdownContent onLinkClick={onLinkClick}>{children}</MarkdownContent>
                {isStreaming && (
                  <Box component="span" sx={streamingCursorSx} />
                )}
              </Box>
            ) : (
              <Typography
                dir="auto"
                variant="body1"
                sx={{
                  textAlign: 'start',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {children}
                {isStreaming && <Box component="span" sx={streamingCursorSx} />}
              </Typography>
            )}
          </Box>

          {timestamp && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                textAlign: isUser ? 'end' : 'start',
                marginBlockStart: 0.5,
                paddingInline: 0.5,
              }}
            >
              {timestamp}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

ChatBubble.displayName = 'ChatBubble';

/**
 * Pre-styled wrapper for a chat message list
 */
export interface ChatContainerProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * ChatContainer - Container for chat bubbles with proper spacing
 *
 * @example
 * ```tsx
 * <ChatContainer>
 *   <ChatBubble sender="bot">Welcome!</ChatBubble>
 *   <ChatBubble sender="user">Hello!</ChatBubble>
 * </ChatContainer>
 * ```
 */
export function ChatContainer({ children, sx }: ChatContainerProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        paddingInline: 2,
        paddingBlock: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

ChatContainer.displayName = 'ChatContainer';
